/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import LoadingPage from "components/common_components/LoadingPage";
import { useDispatch } from "react-redux";
import { notifyError, notifyInfo } from "redux/actions/Notifications";
import clsx from "clsx";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { TextField } from "@material-ui/core";

import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import FormsContext from "../FormsContext";
import { hasElements } from "utils/arrays";
import { ConfirmDialog } from "components/common_components/Dialogs";
import SearchByToggle, { SearchBy } from "./SearchByToggle";
import { searchFileCaseColumns } from "./fileCaseColumns";

const FileCaseSearch = ({ form, visible, onBackClick, onFileCaseSelected, onSearchByChange }) => {
    const dispatch = useDispatch();
    const [filterTemp, setFilterTemp] = useState("");
    const [filter, setFilter] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pageableData, setPageableData] = useState(undefined);
    const { FormClients } = useContext(FormsContext);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    function searchFileCases(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        FormClients.fileCases.search({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    useEffect(() => {
        setFilterTemp("");
        setFilter("");
        setData([]);

        return () => {
            setData([]);
        };
    }, [visible, form]);

    function filterBuilder(value = filter) {
        return value ? { search: value } : {};
    }

    const onSearchClick = () => {
        const value = filterTemp.trim();

        if (!value || !form || !visible) {
            return;
        }

        setFilter(value);
        searchFileCases({
            filter: filterBuilder(value),
            pageNumber: 0,
            pageSize: 5,
        });
    };

    const onFilterKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onSearchClick();
        }
    };

    const onRowClickHandler = (item) => {
        if (item.clientEnabled) {
            onFileCaseSelected(item);
        } else {
            setConfirmDialogProps((prev) => ({
                ...prev,
                open: true,
                title: "Client disabled",
                content: `${item.clientFullName} is a disabled client. Do you want to continue anyway?`,
                primaryButtonText: "Yes, continue",
                onPrimaryClick: () => {
                    onFileCaseSelected(item);
                    prev.onClose();
                },
            }));
        }
    };

    return (
        <div
            className={clsx("layout clients-container", {
                visible: visible,
                invisible: !visible,
            })}
        >
            {visible && (
                <>
                    <Button secondary link onClick={onBackClick}>
                        <Icon name="arrow" color="secondary" size="xs" />
                        <span>Back</span>
                    </Button>
                    <hr />
                    <div className="step-title">
                        <h3>Case</h3>
                        <SearchByToggle value={SearchBy.Case} onChange={onSearchByChange} />
                    </div>
                    <small>
                        <i>Select the case; its client will be used for the form</i>
                    </small>

                    <div className="quick-filter">
                        {isLoading && <Icon name="loading" color="secondary" size="sm" />}
                        {!isLoading && <Icon name="search" color="secondary" size="sm" />}
                        <TextField
                            type="text"
                            placeholder="Type the file number or the client's name here..."
                            value={filterTemp}
                            onChange={(e) => setFilterTemp(e.target.value)}
                            onKeyDown={onFilterKeyDown}
                            disabled={isLoading}
                            fullWidth
                        />
                        <Button primary small onClick={onSearchClick} disabled={isLoading || !filterTemp.trim()}>
                            Search
                        </Button>
                    </div>

                    <div className="clients">
                        {isLoading && <LoadingPage />}
                        {!isLoading && hasElements(data) && (
                            <EnhancedTable
                                headCells={searchFileCaseColumns}
                                data={data}
                                dense={false}
                                keyColumnName="fileCaseKey"
                                selectable={false}
                                deletable={false}
                                filterable={false}
                                orderable={false}
                                onRowClick={onRowClickHandler}
                                onRequestData={searchFileCases}
                                filterBuilder={filterBuilder}
                                pageableData={pageableData}
                            />
                        )}
                    </div>

                    <ConfirmDialog {...confirmDialogProps} />
                </>
            )}
        </div>
    );
};

FileCaseSearch.propTypes = {
    form: PropTypes.object,
    visible: PropTypes.bool,
    onBackClick: PropTypes.func,
    onFileCaseSelected: PropTypes.func,
    onSearchByChange: PropTypes.func,
};

FileCaseSearch.defaultProps = {
    form: {},
    visible: false,
    onBackClick: () => {},
    onFileCaseSelected: () => {},
    onSearchByChange: () => {},
};

export default FileCaseSearch;
